import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Award,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  Lightbulb,
  Mic,
  User,
  Video,
} from 'lucide-react';
import { apiRequest } from '../lib/api';
import { ListShell } from '../components/ListShell';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { formatDate } from '../lib/format';
import {
  DEMO_DECISION_LABEL,
  type ApplicationDetailResponse,
  type CandidateStatusResponse,
} from '../components/hiring';

export const Route = createFileRoute('/_authed/candidate/demos')({
  component: CandidateDemosPage,
});

interface CandidateDemoView {
  id: string;
  sequenceNo: number;
  scheduledAt: string;
  heldAt: string | null;
  topic: string | null;
  audience: string | null;
  decision: ApplicationDetailResponse['demos'][number]['decision'];
  // H-5: evaluator's display name only — never their id/email/phone or any
  // HR-internal scores/notes. Nullable: the backend left-joins users, so a
  // future hard-deleted evaluator account surfaces as null.
  evaluatorName: string | null;
}

interface CandidateDemosResponse {
  items: CandidateDemoView[];
}

const PREP_CHECKLIST: ReadonlyArray<{
  icon: React.ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  title: string;
  body: string;
}> = [
  {
    icon: Video,
    title: 'Camera on, well-lit',
    body: 'Sit facing a light source. Keep the camera at eye level. Quiet room, plain background if possible.',
  },
  {
    icon: Mic,
    title: 'Test your mic',
    body: 'Use a headset if you can — built-in laptop mics often pick up background noise. Join 5 minutes early to confirm audio.',
  },
  {
    icon: BookOpen,
    title: 'Prepare your material',
    body: 'Have the topic outline written down. A short hello, the lesson body, and a one-line wrap-up. Plan for ~15 minutes per demo.',
  },
  {
    icon: Lightbulb,
    title: 'Teach, do not lecture',
    body: 'Ask the audience questions. Pause for answers. Show you can hold attention, not just deliver content.',
  },
];

function CandidateDemosPage() {
  const statusQuery = useQuery({
    queryKey: ['me', 'hiring', 'status'] as const,
    queryFn: ({ signal }) =>
      apiRequest<CandidateStatusResponse>('/api/me/hiring/status', { signal }),
  });

  const demosQuery = useQuery({
    queryKey: ['me', 'hiring', 'demos'] as const,
    queryFn: ({ signal }) =>
      apiRequest<CandidateDemosResponse>('/api/me/hiring/demos', { signal }),
    // Only enabled once we know the candidate is at a stage that can have
    // demos. Avoids a noisy fetch + 404-ish render on early stages.
    enabled: statusQuery.data?.application.pipelineStage !== undefined,
  });

  const stage = statusQuery.data?.application.pipelineStage ?? null;
  const showSchedule = stage === 'demo_scheduled' || stage === 'demo_in_progress';
  const demos = demosQuery.data?.items ?? [];

  return (
    <ListShell
      title="Your demo classes"
      description="Three short live classes that show us how you teach. You'll need to take all three within three days of being notified."
      primaryAction={
        <Link
          to="/candidate/status"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to my application
        </Link>
      }
    >
      <section className="mx-auto max-w-3xl space-y-6">
        <GatedSection
          error={statusQuery.error}
          title="Demos"
          onRetry={() => statusQuery.refetch()}
        >
          {statusQuery.isLoading || demosQuery.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : !showSchedule ? (
            <EmptyState
              icon={<Award size={28} aria-hidden="true" />}
              title="No demos scheduled yet"
              description="Once you pass the interview, the academy will schedule three demo classes. You'll see the dates here as soon as they're assigned."
            />
          ) : demos.length === 0 ? (
            <EmptyState
              icon={<CalendarClock size={28} aria-hidden="true" />}
              title="Schedule is on its way"
              description="The academic team is preparing your three demo slots. Once they're set you'll see each demo's date, topic, and audience here. Check back shortly — you'll also get an email."
            />
          ) : (
            <DemosList demos={demos} />
          )}
        </GatedSection>

        {showSchedule ? <PrepGuide /> : null}
      </section>
    </ListShell>
  );
}

function DemosList({ demos }: { demos: ReadonlyArray<CandidateDemoView> }) {
  return (
    <ol className="space-y-3">
      {demos.map((d) => {
        const isHeld = d.heldAt !== null;
        const isDecided = d.decision !== 'pending';
        return (
          <li
            key={d.id}
            className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gold-100 text-xs font-bold tabular-nums text-gold-800 dark:bg-gold-700/40 dark:text-gold-50">
                    {d.sequenceNo}
                  </span>
                  <p className="font-display text-base font-semibold">
                    Demo {d.sequenceNo} of 3
                  </p>
                </div>
                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[color:var(--color-text-muted)] tabular-nums">
                  <CalendarClock size={12} aria-hidden="true" />
                  {isHeld
                    ? `Held ${formatDate(d.heldAt as string, { withTime: true })}`
                    : `Scheduled ${formatDate(d.scheduledAt, { withTime: true })}`}
                </p>
                {d.topic ? (
                  <p className="mt-2 text-sm">
                    <span className="text-[color:var(--color-text-muted)]">Topic: </span>
                    <strong>{d.topic}</strong>
                  </p>
                ) : null}
                {d.audience ? (
                  <p className="text-xs text-[color:var(--color-text-muted)]">
                    Audience: {d.audience}
                  </p>
                ) : null}
                {/* H-5: display name only — never id/email/phone. Omit the
                    line entirely when the name is null (degrades gracefully). */}
                {d.evaluatorName ? (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-[color:var(--color-text-muted)]">
                    <User size={12} aria-hidden="true" />
                    Evaluated by: {d.evaluatorName}
                  </p>
                ) : null}
              </div>
              <DecisionPill decision={d.decision} held={isHeld} decided={isDecided} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function DecisionPill({
  decision,
  held,
  decided,
}: {
  decision: CandidateDemoView['decision'];
  held: boolean;
  decided: boolean;
}) {
  if (!held) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Upcoming
      </span>
    );
  }
  if (!decided) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Under review
      </span>
    );
  }
  const isPass = decision === 'passed';
  return (
    <span
      className={
        isPass
          ? 'inline-flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-2 py-0.5 text-[11px] uppercase tracking-wide text-[color:var(--color-success)]'
          : 'inline-flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 px-2 py-0.5 text-[11px] uppercase tracking-wide text-[color:var(--color-danger)]'
      }
    >
      {isPass ? <CheckCircle2 size={11} aria-hidden="true" /> : null}
      {DEMO_DECISION_LABEL[decision]}
    </span>
  );
}

function PrepGuide() {
  return (
    <section className="rounded-2xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] p-5 dark:from-gold-700/20">
      <header className="flex items-start gap-3">
        <span className="rounded-full bg-gold-100 p-2 text-gold-800 dark:bg-gold-700/40 dark:text-gold-50">
          <Lightbulb size={18} aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-display text-lg font-semibold">
            How to prepare
          </h3>
          <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
            A short checklist the academy uses to evaluate every demo.
          </p>
        </div>
      </header>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {PREP_CHECKLIST.map((item) => {
          const Icon = item.icon;
          return (
            <li
              key={item.title}
              className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-3"
            >
              <div className="flex items-center gap-2 text-[color:var(--color-text)]">
                <Icon size={14} aria-hidden="true" />
                <p className="font-medium text-sm">{item.title}</p>
              </div>
              <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                {item.body}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
